// ===================================================================
// KAIXO - AdminSupportPanel
// ===================================================================
// Panel para admins: lista de tickets con filtros, detalle con chat,
// input para responder (envía como KAIXO Support), cerrar ticket.
// Solo se renderiza si currentUser.isAdmin === true.
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    adminSupportListTickets, adminSupportGetTicket,
    adminSupportReply, adminSupportCloseTicket, adminSupportResetPassword
} from '../../services/supportApi';
import './AdminSupportPanel.css';

const STATUS_LABELS = {
    open: 'Pendientes',
    answered: 'Respondidos',
    closed: 'Cerrados'
};

// Cuando `embedded` está activo (uso dentro del AdminPanel principal), no
// portalizamos a document.body — el overlay del AdminPanel ya cubre la
// pantalla; un doble portal hacía que el panel quedara detrás (invisible)
// y la sección apareciera en blanco. En modo embebido renderizamos inline
// y omitimos el overlay y la X (la X duplicaría la del AdminPanel).
function AdminSupportPanel({ currentUser, onClose, embedded = false }) {
    const [statusFilter, setStatusFilter] = useState('open');
    const [tickets, setTickets] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [selected, setSelected] = useState(null); // ticket + conversation
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [tempPasswordResult, setTempPasswordResult] = useState(null);
    const [resetting, setResetting] = useState(false);

    const loadList = useCallback(async () => {
        setLoadingList(true);
        try {
            const { data } = await adminSupportListTickets(statusFilter);
            setTickets(data.tickets || []);
        } catch (e) {
            setError(e?.response?.data?.error || 'No se pudo cargar la lista');
        } finally {
            setLoadingList(false);
        }
    }, [statusFilter]);

    useEffect(() => { loadList(); }, [loadList]);

    const openTicket = async (id) => {
        setLoadingDetail(true);
        setError('');
        try {
            const { data } = await adminSupportGetTicket(id);
            setSelected(data);
        } catch (e) {
            setError(e?.response?.data?.error || 'No se pudo cargar el ticket');
        } finally {
            setLoadingDetail(false);
        }
    };

    const sendReply = async () => {
        if (!selected?.ticket?._id || !reply.trim() || sending) return;
        setSending(true);
        try {
            await adminSupportReply(selected.ticket._id, reply.trim());
            setReply('');
            // Recargar detalle y lista
            await openTicket(selected.ticket._id);
            await loadList();
        } catch (e) {
            setError(e?.response?.data?.error || 'Error al enviar');
        } finally {
            setSending(false);
        }
    };

    const resetPassword = async () => {
        if (!selected?.ticket?._id || resetting) return;
        if (!window.confirm(`¿Resetear la contraseña de ${selected.ticket.user?.name || 'este usuario'}? Se generará una temporal que deberás enviarle por este chat.`)) return;
        setResetting(true);
        try {
            const { data } = await adminSupportResetPassword(selected.ticket._id);
            setTempPasswordResult({
                tempPassword: data.tempPassword,
                userName: data.userName || selected.ticket.user?.name || ''
            });
        } catch (e) {
            setError(e?.response?.data?.error || 'Error al resetear contraseña');
        } finally {
            setResetting(false);
        }
    };

    const copyTempPassword = () => {
        if (!tempPasswordResult) return;
        try { navigator.clipboard.writeText(tempPasswordResult.tempPassword); } catch {}
    };

    const insertTempIntoReply = () => {
        if (!tempPasswordResult) return;
        const msg = `Hola ${tempPasswordResult.userName}, tu contraseña temporal es: ${tempPasswordResult.tempPassword}\n\nEntra con ella y cámbiala desde Ajustes en cuanto puedas.`;
        setReply(msg);
        setTempPasswordResult(null);
    };

    const closeTicket = async () => {
        if (!selected?.ticket?._id) return;
        if (!window.confirm('¿Cerrar este ticket?')) return;
        try {
            await adminSupportCloseTicket(selected.ticket._id);
            setSelected(null);
            await loadList();
        } catch (e) { setError(e?.response?.data?.error || 'Error al cerrar'); }
    };

    const content = (
        <div className={embedded ? 'adm-sup-embedded' : 'adm-sup-overlay'} onClick={embedded ? undefined : onClose}>
            <div className="adm-sup-modal" onClick={e => e.stopPropagation()}>
                {!embedded && (
                    <div className="adm-sup-header">
                        <h2>Soporte · {STATUS_LABELS[statusFilter]}</h2>
                        <button className="adm-sup-close" onClick={onClose}>×</button>
                    </div>
                )}

                <div className="adm-sup-body">
                    {/* Columna izquierda: lista */}
                    <div className="adm-sup-list-col">
                        <div className="adm-sup-filters">
                            {Object.entries(STATUS_LABELS).map(([k, label]) => (
                                <button
                                    key={k}
                                    className={`adm-sup-filter ${statusFilter === k ? 'active' : ''}`}
                                    onClick={() => { setStatusFilter(k); setSelected(null); }}
                                >{label}</button>
                            ))}
                        </div>

                        {loadingList && <div className="adm-sup-dim">Cargando…</div>}
                        {!loadingList && tickets.length === 0 && (
                            <div className="adm-sup-dim">Sin tickets</div>
                        )}
                        {!loadingList && tickets.map(t => (
                            <button
                                key={t._id}
                                className={`adm-sup-item ${selected?.ticket?._id === t._id ? 'selected' : ''}`}
                                onClick={() => openTicket(t._id)}
                            >
                                <div className="adm-sup-item-head">
                                    <strong>{t.user?.name || '—'}</strong>
                                    <span className="adm-sup-tag">{t.userLanguage || '?'}</span>
                                </div>
                                <div className="adm-sup-item-preview">{t.firstMessage || '(sin mensaje)'}</div>
                                <div className="adm-sup-item-time">
                                    {new Date(t.openedAt).toLocaleString()}
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Columna derecha: detalle */}
                    <div className="adm-sup-detail-col">
                        {!selected && <div className="adm-sup-placeholder">Selecciona un ticket a la izquierda.</div>}
                        {loadingDetail && <div className="adm-sup-dim">Cargando ticket…</div>}
                        {selected && (
                            <>
                                <div className="adm-sup-detail-head">
                                    <div>
                                        <strong>{selected.ticket.user?.name || '—'}</strong>
                                        <span className="adm-sup-tag" style={{ marginLeft: 8 }}>{selected.ticket.userLanguage}</span>
                                        {selected.ticket.user?.country && (
                                            <span className="adm-sup-tag" style={{ marginLeft: 6 }}>{selected.ticket.user.country}</span>
                                        )}
                                    </div>
                                    <div className="adm-sup-detail-actions">
                                        {selected.ticket.status !== 'closed' && (
                                            <>
                                                <button
                                                    className="adm-sup-action"
                                                    onClick={resetPassword}
                                                    disabled={resetting}
                                                    title="Generar contraseña temporal para este usuario"
                                                >
                                                    {resetting ? 'Generando…' : '🔑 Resetear contraseña'}
                                                </button>
                                                <button className="adm-sup-danger" onClick={closeTicket}>Cerrar ticket</button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="adm-sup-chat">
                                    {(selected.conversation || []).length === 0 && (
                                        <div className="adm-sup-dim">Sin historial previo.</div>
                                    )}
                                    {(selected.conversation || []).map(m => {
                                        const fromSupport = String(m.sender) !== String(selected.ticket.user?._id);
                                        return (
                                            <div key={m._id} className={`adm-sup-msg ${fromSupport ? 'from-support' : 'from-user'}`}>
                                                <div className="adm-sup-msg-text">{m.originalText || m.text || ''}</div>
                                                <div className="adm-sup-msg-time">{new Date(m.createdAt || m.timestamp).toLocaleString()}</div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {selected.ticket.status !== 'closed' && (
                                    <div className="adm-sup-reply">
                                        <textarea
                                            value={reply}
                                            onChange={e => setReply(e.target.value)}
                                            placeholder="Tu respuesta (llegará como mensaje de KAIXO Support)..."
                                            rows={3}
                                        />
                                        <button
                                            className="adm-sup-primary"
                                            disabled={!reply.trim() || sending}
                                            onClick={sendReply}
                                        >{sending ? 'Enviando…' : 'Responder'}</button>
                                    </div>
                                )}

                                {error && <div className="adm-sup-error">{error}</div>}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal: contraseña temporal generada (se muestra UNA vez) */}
            {tempPasswordResult && (
                <div className="adm-sup-temp-overlay" onClick={() => setTempPasswordResult(null)}>
                    <div className="adm-sup-temp-card" onClick={e => e.stopPropagation()}>
                        <div className="adm-sup-temp-icon">🔑</div>
                        <h3>Contraseña temporal generada</h3>
                        <p>Cópiala AHORA. No se mostrará de nuevo. Envíasela al usuario por este mismo chat.</p>
                        <div className="adm-sup-temp-value">{tempPasswordResult.tempPassword}</div>
                        <div className="adm-sup-temp-actions">
                            <button className="adm-sup-action" onClick={copyTempPassword}>📋 Copiar</button>
                            <button className="adm-sup-primary" onClick={insertTempIntoReply}>
                                Insertar en respuesta
                            </button>
                        </div>
                        <button className="adm-sup-temp-close" onClick={() => setTempPasswordResult(null)}>
                            Ya la copié
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    if (embedded) return content;
    return ReactDOM.createPortal(content, document.body);
}

export default AdminSupportPanel;
